import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  connectVkGroup,
  deleteChannel,
  getChannel,
  getChannelHealth,
  listChannels,
  patchChannel,
  pauseChannel,
  registerVkPersonal,
  resolveChannel,
  resumeChannel,
  type ConnectVkGroupBody,
  type PatchChannelBody,
  type RegisterVkPersonalBody,
} from '../api/channels';

export function useChannelList() {
  return useQuery({ queryKey: ['channels'], queryFn: listChannels, refetchOnWindowFocus: false });
}

export function useChannel(id: string | undefined) {
  return useQuery({
    queryKey: ['channels', 'detail', id],
    queryFn: () => getChannel(id as string),
    enabled: Boolean(id),
  });
}

export function useChannelHealth(id: string | undefined) {
  return useQuery({
    queryKey: ['channels', 'health', id],
    queryFn: () => getChannelHealth(id as string),
    enabled: Boolean(id),
    refetchInterval: 15_000,
  });
}

function useInvalidateChannels() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['channels'] });
}

export function useConnectVkGroup() {
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: (body: ConnectVkGroupBody) => connectVkGroup(body),
    onSuccess: invalidate,
  });
}

export function useRegisterVkPersonal() {
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: (body: RegisterVkPersonalBody) => registerVkPersonal(body),
    onSuccess: invalidate,
  });
}

export function usePatchChannel() {
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchChannelBody }) => patchChannel(id, body),
    onSuccess: invalidate,
  });
}

export function usePauseChannel() {
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => pauseChannel(id, reason),
    onSuccess: invalidate,
  });
}

export function useResumeChannel() {
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => resumeChannel(id, reason),
    onSuccess: invalidate,
  });
}

/**
 * Resolves a link / nickname / id to a preview card. A mutation rather than a
 * query: it runs when the user asks for it, never on every keystroke.
 */
export function useResolveChannel() {
  return useMutation({ mutationFn: (query: string) => resolveChannel(query) });
}

export function useDeleteChannel() {
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) => deleteChannel(id, force ?? false),
    onSuccess: invalidate,
  });
}
